import { Navigate, Route, Routes as RouterRoutes, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ForgotPassword from '../components/auth/ForgotPassword';
import GoogleCallback from '../components/auth/GoogleCallback';
import Login from '../components/auth/Login';
import RequestUnlock from '../components/auth/RequestUnlock';
import ResetPassword from '../components/auth/ResetPassword';
import PrivacyPolicyPage from '../pages/legal/PrivacyPolicy';
import TermsOfServicePage from '../pages/legal/TermsOfService';
import { ProtectedRoute } from '../components/ProtectedRoutes';
import { APP_ROUTES } from '../utils/constants/appRoutes';
import {
  AllAuthenticatedUsers,
  DeviceGroupAdminPlusAndViewer,
  OrgAdminAndSuperAdmin,
  SuperAdminAndViewer,
  SuperAdminOnly,
} from '../utils/constants/users';

// Lazy load all page components
const DeviceDetailsPage = lazy(() => import('../pages/device-detail'));
const DevicesPage = lazy(() => import('../pages/devices'));
const DataLogPage = lazy(() => import('../pages/dynamic-data-log'));
const FaultEventLogPage = lazy(() => import('../pages/fault-event-log'));
const GroupsPage = lazy(() => import('../pages/groups'));
const GroupDetailsPage = lazy(() => import('../pages/group-details'));
const UnassignedDevicesPage = lazy(() => import('../pages/unassigned-devices'));
const MultiSpeakProvidersPage = lazy(() => import('../pages/multispeak-providers'));
const NotificationsPage = lazy(() => import('../pages/notifications'));
const AccountSecurityPage = lazy(() => import('../pages/account-security'));
const OrganizationsPage = lazy(() => import('../pages/organizations'));
const OrganizationSettingsPage = lazy(() => import('../pages/organization-settings'));
const PowerLinesPage = lazy(() => import('../pages/power-lines'));
const PowerLineDetailsPage = lazy(() => import('../pages/power-line-details'));
const UsersPage = lazy(() => import('../pages/users'));

// Loading component for lazy-loaded routes
const PageLoader = () => <></>;

const Routes = () => {
  const location = useLocation();

  return (
    <RouterRoutes location={location}>
      <Route
        path={APP_ROUTES.HOME}
        element={
          <ProtectedRoute authorizedUsers={AllAuthenticatedUsers}>
            <Suspense fallback={<PageLoader />}>
              <DevicesPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.CRITICAL_DATA}
        element={
          <ProtectedRoute authorizedUsers={AllAuthenticatedUsers}>
            <Suspense fallback={<PageLoader />}>
              <FaultEventLogPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.DYNAMIC_DATA_LOG}
        element={
          <ProtectedRoute authorizedUsers={AllAuthenticatedUsers}>
            <Suspense fallback={<PageLoader />}>
              <DataLogPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.GROUPS}
        element={
          <ProtectedRoute authorizedUsers={DeviceGroupAdminPlusAndViewer}>
            <Suspense fallback={<PageLoader />}>
              <GroupsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.UNASSIGNED_DEVICES}
        element={
          <ProtectedRoute authorizedUsers={DeviceGroupAdminPlusAndViewer}>
            <Suspense fallback={<PageLoader />}>
              <UnassignedDevicesPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.GROUP_DETAILS}
        element={
          <ProtectedRoute authorizedUsers={DeviceGroupAdminPlusAndViewer}>
            <Suspense fallback={<PageLoader />}>
              <GroupDetailsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.USERS}
        element={
          <ProtectedRoute authorizedUsers={DeviceGroupAdminPlusAndViewer}>
            <Suspense fallback={<PageLoader />}>
              <UsersPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.LOCKED_ACCOUNTS}
        element={<Navigate to={`${APP_ROUTES.USERS}?tab=locked`} replace />}
      />
      <Route
        path={APP_ROUTES.UNLOCK_REQUESTS}
        element={<Navigate to={`${APP_ROUTES.USERS}?tab=unlock-requests`} replace />}
      />
      <Route
        path={APP_ROUTES.MS_PROVIDERS}
        element={
          <ProtectedRoute authorizedUsers={SuperAdminOnly}>
            <Suspense fallback={<PageLoader />}>
              <MultiSpeakProvidersPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.ORGANIZATIONS}
        element={
          <ProtectedRoute authorizedUsers={SuperAdminOnly}>
            <Suspense fallback={<PageLoader />}>
              <OrganizationsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.ORGANIZATION_SETTINGS}
        element={
          <ProtectedRoute authorizedUsers={OrgAdminAndSuperAdmin}>
            <Suspense fallback={<PageLoader />}>
              <OrganizationSettingsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.POWER_LINES}
        element={
          <ProtectedRoute authorizedUsers={SuperAdminAndViewer}>
            <Suspense fallback={<PageLoader />}>
              <PowerLinesPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.POWER_LINE_DETAILS}
        element={
          <ProtectedRoute authorizedUsers={SuperAdminAndViewer}>
            <Suspense fallback={<PageLoader />}>
              <PowerLineDetailsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.DEVICE_DETAILS}
        element={
          <ProtectedRoute authorizedUsers={AllAuthenticatedUsers}>
            <Suspense fallback={<PageLoader />}>
              <DeviceDetailsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.NOTIFICATION_CENTER}
        element={
          <ProtectedRoute authorizedUsers={AllAuthenticatedUsers}>
            <Suspense fallback={<PageLoader />}>
              <NotificationsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={APP_ROUTES.ACCOUNT_SECURITY}
        element={
          <ProtectedRoute authorizedUsers={AllAuthenticatedUsers}>
            <Suspense fallback={<PageLoader />}>
              <AccountSecurityPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route path={APP_ROUTES.AUTH} element={<Login />} />
      <Route path={APP_ROUTES.GOOGLE_CALLBACK} element={<GoogleCallback />} />
      <Route path={APP_ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
      <Route path={APP_ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
      <Route path={APP_ROUTES.REQUEST_UNLOCK} element={<RequestUnlock />} />
      <Route path={APP_ROUTES.TERMS_OF_SERVICE} element={<TermsOfServicePage />} />
      <Route path={APP_ROUTES.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
      <Route path="*" element={<Navigate to={APP_ROUTES.AUTH} replace />} />
    </RouterRoutes>
  );
};

export default Routes;
